import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

const STORAGE_KEY = "atm-staging-indicator-position";
const MARGIN = 12;

const clampPosition = (x, y, width, height) => {
  const maxX = Math.max(MARGIN, window.innerWidth - width - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - height - MARGIN);

  return {
    x: Math.min(Math.max(MARGIN, x), maxX),
    y: Math.min(Math.max(MARGIN, y), maxY),
  };
};

const readSavedPosition = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== "number" || typeof parsed?.y !== "number") {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const StagingIndicator = () => {
  const pillRef = useRef(null);
  const dragRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [dragging, setDragging] = useState(false);

  useLayoutEffect(() => {
    const element = pillRef.current;
    if (!element) return;

    const { width, height } = element.getBoundingClientRect();
    const saved = readSavedPosition();
    const initial = saved
      ? clampPosition(saved.x, saved.y, width, height)
      : {
          x: (window.innerWidth - width) / 2,
          y: window.innerHeight - height - 28,
        };

    setPosition(initial);
  }, []);

  useEffect(() => {
    const onResize = () => {
      const element = pillRef.current;
      if (!element) return;

      const { width, height } = element.getBoundingClientRect();
      setPosition((current) =>
        current ? clampPosition(current.x, current.y, width, height) : current
      );
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = (event) => {
    if (event.button !== 0 || !position) return;

    const element = pillRef.current;
    element.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - position.x,
      offsetY: event.clientY - position.y,
    };
    setDragging(true);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    const element = pillRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !element) return;

    const { width, height } = element.getBoundingClientRect();
    setPosition(
      clampPosition(
        event.clientX - drag.offsetX,
        event.clientY - drag.offsetY,
        width,
        height
      )
    );
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    dragRef.current = null;
    setDragging(false);
    setPosition((current) => {
      if (current) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
        } catch {
          // Position still updates for this session if storage is unavailable.
        }
      }
      return current;
    });
  };

  return (
    <div
      ref={pillRef}
      className={`staging-indicator${dragging ? " is-dragging" : ""}`}
      style={
        position
          ? { left: position.x, top: position.y }
          : { left: 0, top: 0, visibility: "hidden" }
      }
      role="status"
      aria-label="Staging environment. Drag to reposition."
      title="Drag to move"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <span className="staging-indicator__grip" aria-hidden="true">
        {Array.from({ length: 6 }, (_, index) => (
          <span key={index} />
        ))}
      </span>
      <span className="staging-indicator__dot" aria-hidden="true" />
      <span className="staging-indicator__label">Staging Environment</span>
    </div>
  );
};

export default StagingIndicator;
